import type { PageHelp } from "../../help/types";

export const hrPageHelp: PageHelp = {
  title: "HR",
  intro:
    "The HR page is where client HR staff and GrECo brokers run a client's benefit programme day to day: maintain employees and dependents, grant package eligibility, approve enrollments and export reports. Pick a client first; every view and action below works on that one client.",
  sections: [
    "Getting around",
    "Choosing the client",
    "Switching views",
    "Toolbar actions",
    "Summary view",
    "Benefits view",
    "Employees view",
    "Employee row actions",
    "Enrollments and dependents",
    "Add or edit an employee",
    "Dependents dialog",
    "Package eligibility dialog",
    "Edit enrollment dialog",
    "Import employees",
    "Enrollments report by period",
    "Export consents",
    "Bulk Management view",
    "Bulk dialogs",
    "Employee tags dialog",
    "Analytics view",
    "Action History view",
    "User Assignments view",
    "Security view",
  ],
  contexts: {
    "client-selected":
      'Select a client in the "Select Company" box at the top of the page.',
    "views-with-only-active":
      'Select a client and open the "Benefits", "Employees" or "Security" view.',
    "employees-or-bulk":
      'Select a client and open the "Employees" or "Bulk Management" view.',
    "actions-menu":
      'Open the "Employees" or "Bulk Management" view and click "Actions" in the toolbar.',
    "summary-view":
      'Select a client and open the "Summary" view (it opens by default).',
    "benefits-view": 'Select a client and open the "Benefits" view.',
    "settings-menu": "Open the settings menu in the app header.",
    "employees-view": 'Select a client and open the "Employees" view.',
    "expanded-row":
      'In the "Employees" view, click an employee\'s name to expand the row. Only rows with enrollments or dependents expand.',
    "employee-dialog":
      'Click "Add Employee" in the toolbar, or the edit (pencil) icon in an employee row.',
    "add-employee-dialog": 'Click "Add Employee" in the toolbar.',
    "edit-employee-dialog":
      'In the "Employees" view, click the edit (pencil) icon in an employee row.',
    "dependents-dialog":
      'Open an employee with the edit icon and click "Manage Dependents", or click the edit icon on a dependent line in an expanded row.',
    "eligibility-dialog":
      'Click the "Manage Package Eligibility" icon in an employee row or on a dependent line.',
    "enrollment-dialog":
      'Expand an employee row and click "Edit Enrollment" (note icon) on a package line.',
    "import-dialog": 'Open "Actions" in the toolbar and choose "Import From Template".',
    "report-period-dialog":
      'Open "Actions" in the toolbar and choose "Employee Enrollments Report by time period".',
    "export-consents-dialog":
      'In the "Employees" view, select employees and click "Export Consents" above the table, or click the export icon in one row.',
    "bulk-view":
      'Select a client and open the "Bulk Management" view (admin roles only).',
    "bulk-eligibility-dialog":
      'In "Bulk Management", select people and packages, then choose "Bulk Actions" › "Make Eligible".',
    "bulk-enroll-dialog":
      'In "Bulk Management", select people and packages, then choose "Bulk Actions" › "Enroll".',
    "bulk-cancel-dialog":
      'In "Bulk Management", open a package\'s "More options" menu and choose "Cancel Enrollments", or "Cancel Enrollment" on one person under a package.',
    "bulk-remove-dialog":
      'In "Bulk Management", choose "Bulk Actions" › "Remove Eligibilities" or "Remove Enrollments".',
    "tags-dialog":
      'In "Bulk Management", select employees and choose "Manage Tags" › "Add Tags" or "Remove Tags".',
    "analytics-view": 'Select a client and open the "Analytics" view.',
    "history-view": 'Select a client and open the "Action History" view.',
    "user-assignments-view":
      'Select a client and open the "User Assignments" view (admin roles only).',
    "security-view": 'Select a client and open the "Security" view.',
  },
  tips: [
    "Fields highlighted in yellow in the employee and dependent forms were changed by the employee in the employee app. Review them and click \"Confirm\" (the Save button shows this label while nothing was edited) to accept the change and clear the Needs Attention flag.",
    "Read-only roles see the edit, eligibility and delete buttons greyed out. Buttons that are hidden entirely for your role carry a role badge in this guide.",
    "Bulk enrollment changes and employee tags can only be saved by GrECo users; the server rejects these requests from client accounts.",
    "Every Excel import and every bulk enrollment operation is logged in Action History together with its input file and a result report.",
  ],
  steps: [
    // ===== Getting around =====
    {
      selector: '[data-help="subheader-refresh"]',
      section: "Getting around",
      title: "Reload the current view",
      body: "Fetches fresh data for the view you are on (employees, benefits, history, user assignments or security) without reloading the whole app. Use it after a colleague or the employee app has changed something. Without a selected client it reloads the client list.",
    },
    {
      selector: '[data-help="needs-attention-toggle"]',
      section: "Getting around",
      title: "Open the Needs Attention list",
      body: "The bell opens the Needs Attention page, which collects records that wait for HR, such as data changed in the employee app and enrollments or cancellations waiting for approval, across your clients. The red badge counts the open items for the selected client. Click the bell again to come back.",
    },
    {
      selector: '[data-help="subheader-ask-question"]',
      section: "Getting around",
      title: "Ask the AI assistant a question",
      body: '"Ask Question" opens the chat assistant, which answers questions using the selected client\'s benefit documents. It stays disabled until a client is selected.',
    },
    {
      selector: '[data-help="only-active-switch"]',
      section: "Getting around",
      context: "views-with-only-active",
      title: "Show only benefits that are running today",
      body: '"Only Active" (on by default) hides benefits whose policy period has not started yet or has already ended, together with their enrollments. It appears in the Benefits, Employees and Security views and applies to all three. Turn it off to see past and future benefits.',
    },
    // ===== Choosing the client =====
    {
      selector: '[data-help="subheader-client-select"]',
      section: "Choosing the client",
      title: "Choose the client to work on",
      body: 'Type in "Select Company" to filter and pick a client; the list shows only clients you have access to. The toolbar, views and actions appear once a client is selected. Picking another client clears the selections and the employee search.',
    },
    {
      selector: '[data-help="edit-client-menu"]',
      section: "Choosing the client",
      context: "client-selected",
      minRole: "admin",
      title: "Edit the client's details",
      body: "The client name in the toolbar opens a menu to edit the client's details, the message employees see and the client documents. Deleting the client from the same menu works for SuperAdmin only.",
    },
    // ===== Switching views =====
    {
      selector: '[data-help="hr-tab-summary"]',
      section: "Switching views",
      context: "client-selected",
      title: "Open the Summary dashboard",
      body: "Shows headcount, enrollment counts and costs for the selected client, with filters for period, packages, currency and payment. This view opens by default.",
    },
    {
      selector: '[data-help="hr-tab-benefits"]',
      section: "Switching views",
      context: "client-selected",
      title: "Open the client's benefits",
      body: "Lists the benefits implemented for the client, as cards or as a table. Only admin roles can change them here; everyone else sees them in preview mode.",
    },
    {
      selector: '[data-help="hr-tab-employees"]',
      section: "Switching views",
      context: "client-selected",
      title: "Open the employee list",
      body: "The main working view: a table of all employees with their dependents and enrollments, plus row actions to edit people, grant eligibility and approve requests.",
    },
    {
      selector: '[data-help="hr-tab-bulk"]',
      section: "Switching views",
      context: "client-selected",
      minRole: "admin",
      title: "Open Bulk Management",
      body: "Two trees side by side, people on the left and packages on the right, to make many people eligible, enroll or cancel them in one go, and to manage employee tags.",
    },
    {
      selector: '[data-help="hr-tab-analytics"]',
      section: "Switching views",
      context: "client-selected",
      title: "Open the Analytics report",
      body: "Shows the embedded Power BI report for the selected client.",
    },
    {
      selector: '[data-help="hr-tab-action-history"]',
      section: "Switching views",
      context: "client-selected",
      title: "Open the Action History",
      body: "Lists Excel imports and bulk enrollment operations run for the client, with who ran them, when, and the documents they produced.",
    },
    {
      selector: '[data-help="hr-tab-user-assignments"]',
      section: "Switching views",
      context: "client-selected",
      title: "Open User Assignments",
      body: "Shows which users have access to this client and with which role. The tab is visible to SuperAdmin and to global, country and company admins (including their read-only variants), not to HR roles.",
    },
    {
      selector: '[data-help="hr-tab-security"]',
      section: "Switching views",
      context: "client-selected",
      title: "Open Security",
      body: "A grid of users against benefits to decide who may view or edit each benefit. Visible to admin roles (not their read-only variants) and to HR company admins with user-assignment rights.",
    },
    // ===== Toolbar actions =====
    {
      selector: '[data-help="hr-add-employee"]',
      section: "Toolbar actions",
      context: "employees-or-bulk",
      title: "Add an employee",
      body: "Opens an empty employee form where you can also add dependents and documents in one go. Read-only roles see the button disabled.",
    },
    {
      selector: '[data-help="hr-actions-menu"]',
      section: "Toolbar actions",
      context: "employees-or-bulk",
      title: "Open the Actions menu",
      body: '"Actions" collects the Excel downloads and the import: the import template, two enrollment reports, the import from template and the consent data export.',
    },
    {
      selector: '[data-help="hr-generate-template"]',
      section: "Toolbar actions",
      context: "actions-menu",
      title: "Download the employee import template",
      body: '"Generate Import Template" downloads an Excel file prepared for this client, with a column group for every package you may see, where each person can be marked "Eligible" or "Enrolled". Fill it in and upload it with "Import From Template".',
    },
    {
      selector: '[data-help="hr-enrollments-report"]',
      section: "Toolbar actions",
      context: "actions-menu",
      title: "Download the current enrollments report",
      body: '"Employee Enrollments Report" downloads an Excel file with one row per employee or dependent and their currently active enrollments that are not waiting for approval, including premium, insurer and policy columns.',
    },
    {
      selector: '[data-help="hr-enrollments-report-period"]',
      section: "Toolbar actions",
      context: "actions-menu",
      title: "Download enrollments for a time period",
      body: 'Opens a dialog to pick a "From" and "To" date and downloads the enrollments whose enrollment date falls in that period, with pro-rata premiums.',
    },
    {
      selector: '[data-help="hr-import-template"]',
      section: "Toolbar actions",
      context: "actions-menu",
      title: "Import employees from Excel",
      body: '"Import From Template" opens the upload dialog for a filled-in import template. Read-only roles see it disabled.',
    },
    {
      selector: '[data-help="hr-export-consent-data"]',
      section: "Toolbar actions",
      context: "actions-menu",
      title: "Download the consent status report",
      body: '"Export Consent Data" downloads an Excel report of which consents each employee has given per enrollment. To download the signed consent documents themselves, use "Export Consents" in the Employees view.',
    },
    // ===== Summary view =====
    {
      selector: '[data-help="hr-summary-period"]',
      section: "Summary view",
      context: "summary-view",
      title: "Set the reporting period",
      body: "Drag the two handles to choose the first and last month; the period runs from the 1st of the start month to the 1st of the end month. The arrows add six earlier or later months to the slider. Costs count only the instalments due within this period.",
    },
    {
      selector: '[data-help="hr-summary-packages"]',
      section: "Summary view",
      context: "summary-view",
      title: "Choose the packages to include",
      body: 'The tree lists benefit categories, benefits and their packages; ticking a parent ticks everything below it. Enrollment counts, costs and charts include only the ticked packages. Use "Select All" to include everything.',
    },
    {
      selector: '[data-help="hr-summary-currency"]',
      section: "Summary view",
      context: "summary-view",
      title: "Filter or convert by currency",
      body: 'Pick one currency to count only packages priced in it, or "All Currencies" to convert everything to the default currency of your country. The "Convert to" box below shows the totals in another currency using the current exchange rates.',
    },
    {
      selector: '[data-help="hr-summary-payment-type"]',
      section: "Summary view",
      context: "summary-view",
      title: "Filter by payment type and method",
      body: 'Limit the figures to one payment type; the "Payment Method" box below does the same for monthly, quarterly, annual or one-time payments. The payment method also decides how many instalments fall into the period.',
    },
    {
      selector: '[data-help="hr-summary-historic"]',
      section: "Summary view",
      context: "summary-view",
      title: "Show the data as it was on a past date",
      body: 'Tick "Historic report" and pick a date to load the client\'s data as it stood on that day. Turning it on or changing the date clears the package selection, so tick the packages again.',
    },
    {
      selector: '[data-help="hr-summary-headcount"]',
      section: "Summary view",
      context: "summary-view",
      title: "Read the active headcount",
      body: '"Active Employees" counts employees employed during the period (employed before its end and not exited before its start). "Active Dependents" counts those employees\' dependents.',
    },
    {
      selector: '[data-help="hr-summary-enrollments"]',
      section: "Summary view",
      context: "summary-view",
      title: "Read the enrollment counts",
      body: 'Shown as enrolled / total for the ticked packages: the first number counts enrollments with an enrollment date, the second adds the eligibilities that are not enrolled yet. "Dependent Enrollments" works the same way for dependents.',
    },
    {
      selector: '[data-help="hr-summary-costs"]',
      section: "Summary view",
      context: "summary-view",
      title: "Read company and employee costs",
      body: '"Company Costs" and "Employee Costs" add up each enrollment\'s company and employee share for every instalment due in the period, ending at cancellation or at the end of the policy. The two charts below split the same amounts by category for employees and dependents.',
    },
    // ===== Benefits view =====
    {
      selector: '[data-help="hr-benefits-list"]',
      section: "Benefits view",
      context: "benefits-view",
      title: "Browse the client's benefits",
      body: "Shows every benefit implemented for the client with its packages, policy period and documents. Only admin roles can edit here, everyone else sees a preview; switch between cards and table in the settings menu.",
    },
    {
      selector: '[data-help="benefit-cards-save-order"]',
      section: "Benefits view",
      context: "benefits-view",
      title: "Save your card order",
      body: '"Save Order" stores the current card order as your personal setting for this client. Other users keep their own order.',
    },
    {
      selector: '[data-help="view-mode-toggle"]',
      section: "Benefits view",
      context: "settings-menu",
      title: "Switch between cards and table",
      body: '"View Mode" switches benefit lists between "List View" (table) and "Grid View" (cards). The choice is saved for your user and applies to all pages with benefit lists.',
    },
    // ===== Employees view =====
    {
      selector: '[data-help="subheader-search"]',
      section: "Employees view",
      context: "employees-view",
      title: "Find an employee by name",
      body: "Filters the table by first and last name as you type; email addresses are not searched. The counter next to the box shows how many employees match.",
    },
    {
      selector: '[data-help="hr-export-consents"]',
      section: "Employees view",
      context: "employees-view",
      title: "Export consent documents for selected employees",
      body: '"Export Consents" stays disabled until you select employees. It opens a dialog; the documents are then zipped and you receive an email with a download link. With every row selected, all employees are exported.',
    },
    {
      selector: '[data-help="hr-resend-invitations"]',
      section: "Employees view",
      context: "employees-view",
      title: "Resend sign-up invitations",
      body: '"Resend Invitations" sends the employee-app invitation email again to every selected employee\'s login address. It stays disabled until at least one employee is selected.',
    },
    {
      selector: '[data-help="hr-column-select"]',
      section: "Employees view",
      context: "employees-view",
      title: "Select employees",
      body: "Tick employees for the bulk buttons above the table; the header checkbox selects or clears all rows. Clicking a row also selects it.",
    },
    {
      selector: '[data-help="hr-column-dependents"]',
      section: "Employees view",
      context: "employees-view",
      title: "See how many dependents an employee has",
      body: "Counts the dependents registered for the employee. Expand the row to see them, or open the employee and use \"Manage Dependents\" to change them.",
    },
    {
      selector: '[data-help="hr-column-enrollments"]',
      section: "Employees view",
      context: "employees-view",
      title: "See how many packages an employee is in",
      body: 'Counts the employee\'s own eligibilities and enrollments (dependents\' packages are listed under the dependent). Expand the row to see each package with its dates and status. Click a column header to sort.',
    },
    // ===== Employee row actions =====
    {
      selector: '[data-help="hr-employee-name"]',
      section: "Employee row actions",
      context: "employees-view",
      optional: true,
      title: "Expand an employee's details",
      body: "Click the name to show the employee's packages and dependents below the row. A red bell means the record needs attention, a green shield means GrECo's sensitive-data consent was given, and the avatar dot shows whether the employee has signed up to the employee app (grey when inactive).",
    },
    {
      selector: '[data-help="hr-employee-resend-invitation"]',
      section: "Employee row actions",
      context: "employees-view",
      optional: true,
      title: "Resend one employee's invitation",
      body: "Sends the employee-app invitation email again to this employee only. The icon becomes active once the row is selected.",
    },
    {
      selector: '[data-help="hr-employee-export-consents"]',
      section: "Employee row actions",
      context: "employees-view",
      optional: true,
      title: "Export one employee's consent documents",
      body: "Opens the consent export for this employee only; you receive an email with a download link.",
    },
    {
      selector: '[data-help="hr-employee-edit"]',
      section: "Employee row actions",
      context: "employees-view",
      optional: true,
      title: "Edit an employee",
      body: "Opens the employee form with all personal data, documents and dependents. The icon turns yellow when the employee changed data or uploaded a document in the employee app. Read-only roles see it disabled.",
    },
    {
      selector: '[data-help="hr-employee-eligibility"]',
      section: "Employee row actions",
      context: "employees-view",
      optional: true,
      title: "Make an employee eligible for a package",
      body: 'Opens "Add Package" to grant the employee eligibility for a package, with dates and payment terms. The same icon appears on each dependent line. Read-only roles see it disabled.',
    },
    {
      selector: '[data-help="hr-employee-delete"]',
      section: "Employee row actions",
      context: "employees-view",
      optional: true,
      minRole: "superadmin",
      title: "Delete an employee",
      body: "Deletes the employee after a confirmation, together with their enrollments. Only SuperAdmin sees this button; to end employment normally, set a Date of Exit instead.",
    },
    // ===== Enrollments and dependents =====
    {
      selector: '[data-help="hr-enrollment-row"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Read an enrollment line",
      body: 'Each line shows category, benefit and package with the policy period, "Eligible From" and, once set, the enrollment and cancellation dates. A light background means eligible only, green enrolled (fading in if the start is in the future), red cancelled and dark grey a package that has ended. A dashed green border marks an enrollment, a dashed red border a cancellation, waiting for approval.',
    },
    {
      selector: '[data-help="hr-enrollment-approve"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Approve an enrollment or cancellation",
      body: 'Appears only on lines waiting for approval ("Approve Enrollment" or "Approve Cancellation"). The confirmation asks for a "Date of Approval", prefilled with the requested enrollment date; when you approve an enrollment, this date is saved as the enrollment date.',
    },
    {
      selector: '[data-help="hr-enrollment-reject"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Reject an enrollment or cancellation",
      body: 'You must enter a "Rejection Comment" before confirming. Rejecting an enrollment removes its enrollment date, and for an upgrade restores the package that was cancelled for it; rejecting a cancellation removes the cancellation date.',
    },
    {
      selector: '[data-help="hr-enrollment-consent-documents"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Review consent documents before approving",
      body: "Opens, read-only, the consent documents attached to an enrollment that waits for approval. Check them before you approve.",
    },
    {
      selector: '[data-help="hr-enrollment-edit"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Edit an enrollment",
      body: "Opens the enrollment with its eligibility, enrollment and cancellation dates, approval flags, payment terms and consent documents. Read-only roles see it disabled.",
    },
    {
      selector: '[data-help="hr-enrollment-delete"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Delete an enrollment",
      body: "Removes the eligibility or enrollment and its documents after a confirmation. Use it for entries made by mistake; to end a running enrollment, set a cancellation date instead.",
    },
    {
      selector: '[data-help="hr-enrollment-age-warning"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Spot an age-limit problem",
      body: "The red calendar icon means the person's age is outside the package's age limits. Hover it for the age and the limits, then correct the date of birth or the package.",
    },
    {
      selector: '[data-help="hr-enrollment-rejection-comment"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Read why a request was rejected",
      body: "Shown on lines whose enrollment or cancellation was rejected; hover it to read the rejection comment.",
    },
    {
      selector: '[data-help="hr-dependent-row"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Find a dependent and their packages",
      body: "Each dependent is listed with name and type, with their package lines nested below. A red warning icon means one of their requests waits for approval, an age limit fails, or their data needs attention.",
    },
    {
      selector: '[data-help="hr-dependent-edit"]',
      section: "Enrollments and dependents",
      context: "expanded-row",
      optional: true,
      title: "Edit a dependent",
      body: "Opens the dependent's form. The icon is green for a dependent added in the employee app and yellow for changed data, both waiting for your review. Read-only roles see it disabled.",
    },
    // ===== Add or edit an employee =====
    {
      selector: '[data-help="hr-employee-form-email"]',
      section: "Add or edit an employee",
      context: "employee-dialog",
      title: "Enter the employee's email",
      body: 'Required. When you add an employee, this address becomes their login for the employee app and receives the invitation. Editing it later changes the contact email only, not the login. "Mobile Phone Number" next to it is required as well.',
    },
    {
      selector: '[data-help="hr-employee-form-employment-date"]',
      section: "Add or edit an employee",
      context: "employee-dialog",
      title: "Set the employment date",
      body: "Required. Package eligibility dates are calculated from it, so set it before granting packages.",
    },
    {
      selector: '[data-help="hr-employee-form-birth-date"]',
      section: "Add or edit an employee",
      context: "employee-dialog",
      title: "Set the date of birth",
      body: "Required; the picker does not allow a date less than 16 years ago. Package age limits are checked against it.",
    },
    {
      selector: '[data-help="hr-employee-form-exit-date"]',
      section: "Add or edit an employee",
      context: "edit-employee-dialog",
      title: "Record that an employee leaves",
      body: 'Set the "Date of Exit" when employment ends. On save the server marks the employee as inactive ("Is Inactive" further down is set only by the system); clearing the date makes them active again. Check the employee\'s enrollments afterwards and cancel any that should end with the employment.',
    },
    {
      selector: '[data-help="hr-employee-form-salary"]',
      section: "Add or edit an employee",
      context: "employee-dialog",
      title: "Record the salary",
      body: 'Optional, with the "Currency" box next to it. Salary is visible to HR only; the employee app never shows it.',
    },
    {
      selector: '[data-help="hr-employee-form-comment"]',
      section: "Add or edit an employee",
      context: "employee-dialog",
      title: "Add an internal comment",
      body: "Free text up to 4000 characters for HR notes. The comment is not shown to the employee.",
    },
    {
      selector: '[data-help="hr-employee-form-attachments"]',
      section: "Add or edit an employee",
      context: "add-employee-dialog",
      title: "Attach documents to a new employee",
      body: 'Upload files into the "Document List"; the eye icon on each file decides whether the employee can see it. The files are saved with the employee.',
    },
    {
      selector: '[data-help="hr-employee-form-add-dependent"]',
      section: "Add or edit an employee",
      context: "add-employee-dialog",
      title: "Add dependents while creating the employee",
      body: '"Add Dependent" adds a dependent block under "Dependent Coverages". Dependent type, first name, last name and date of birth are required for each one.',
    },
    {
      selector: '[data-help="hr-employee-form-documents"]',
      section: "Add or edit an employee",
      context: "edit-employee-dialog",
      title: "Manage the employee's documents",
      body: '"Manage Documents" (or "Add Documents" when there are none) opens the document list, where you upload, rename, hide from the employee or delete files. Changes there are saved immediately. The button is yellow when the employee recently uploaded a document.',
    },
    {
      selector: '[data-help="hr-employee-form-manage-dependents"]',
      section: "Add or edit an employee",
      context: "edit-employee-dialog",
      title: "Manage an existing employee's dependents",
      body: '"Manage Dependents" opens a dialog listing all dependents, each with its own Save button, and lets you add new ones.',
    },
    {
      selector: '[data-help="hr-employee-form-save"]',
      section: "Add or edit an employee",
      context: "employee-dialog",
      title: "Save the employee",
      body: 'Checks the required fields (first and last name, email, mobile phone, employment date, date of birth) and saves. For a record flagged by the employee app the button reads "Confirm" until you edit something; confirming accepts the change and clears the Needs Attention flag.',
    },
    // ===== Dependents dialog =====
    {
      selector: '[data-help="hr-dependents-add"]',
      section: "Dependents dialog",
      context: "dependents-dialog",
      title: "Add a dependent to an existing employee",
      body: '"Add Dependent" opens an empty dependent form at the top of the dialog. Save it with its own Save button.',
    },
    {
      selector: '[data-help="hr-dependent-form-type"]',
      section: "Dependents dialog",
      context: "dependents-dialog",
      title: "Choose the dependent type",
      body: 'Required, for example spouse or child. First name, last name and date of birth are required too; email and phone are optional for dependents.',
    },
    {
      selector: '[data-help="hr-dependent-form-save"]',
      section: "Dependents dialog",
      context: "dependents-dialog",
      title: "Save a dependent",
      body: 'Saves this dependent only; each dependent in the list is saved separately. For a dependent added or changed in the employee app the button reads "Confirm" until you edit something.',
    },
    {
      selector: '[data-help="hr-dependent-form-delete"]',
      section: "Dependents dialog",
      context: "dependents-dialog",
      title: "Delete or discard a dependent",
      body: 'For a new, unsaved dependent the button reads "Cancel" and discards the form. For saved dependents "Delete" is shown to SuperAdmin only; deleting also removes the dependent\'s enrollments.',
    },
    // ===== Package eligibility dialog =====
    {
      selector: '[data-help="hr-eligibility-package"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Choose the package",
      body: 'Lists packages that have not ended and for which the person has no running eligibility or enrollment; with "Only Active" on, only packages that have already started. Picking a package fills in suggested dates and the package\'s payment terms.',
    },
    {
      selector: '[data-help="hr-eligibility-date"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Set the eligibility date",
      body: "Prefilled by the server from the employment date, the policy period and the package's eligibility rules. You can move it, but only between the suggested eligibility date and the suggested end date.",
    },
    {
      selector: '[data-help="hr-eligibility-end-date"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Set the end of eligibility",
      body: "Required and prefilled from the package rules, often the end of the policy period. It cannot be earlier than the suggested eligibility date.",
    },
    {
      selector: '[data-help="hr-eligibility-cancellation"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Set the earliest cancellation date",
      body: '"Cancellation Possibility" is the first date the person may cancel. It is required unless the package counts it from the start of enrollment, in which case it is calculated once the person enrolls.',
    },
    {
      selector: '[data-help="hr-eligibility-auto-enrollment"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Enroll the person automatically",
      body: '"Is Auto Enrollment" marks the eligibility for automatic enrollment, so the person does not have to enroll themselves; ticking it recalculates the dates. It is unavailable for packages that need consent documents.',
    },
    {
      selector: '[data-help="hr-eligibility-approval"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Require approval for enrollment or cancellation",
      body: '"Requires Enrollment Approval" makes the person\'s enrollment wait for HR approval; it is forced on for packages that need consent and off for auto enrollment. "Requires Cancellation Approval" does the same for cancellations. Both start from the package defaults.',
    },
    {
      selector: '[data-help="hr-package-payment-method"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Adjust the payment terms",
      body: 'Payment Method, Payment Type and "Premium/Cost" come from the package and can be changed for this person. The same fields appear when you edit an enrollment.',
    },
    {
      selector: '[data-help="hr-package-paid-by-company"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Split the cost between company and employee",
      body: 'Enter the company share; the employee share is filled in automatically. With "Value Type" set to percentage both must add up to 100, otherwise to the premium, or the form shows "Payments must be equal to package price".',
    },
    {
      selector: '[data-help="hr-eligibility-save"]',
      section: "Package eligibility dialog",
      context: "eligibility-dialog",
      title: "Save the eligibility",
      body: "Creates the eligibility; the person can then enroll in the employee app, or is enrolled automatically. If the person's age is outside the package's age limits, a red notice appears and Save stays disabled until you turn on the switch under it.",
    },
    // ===== Edit enrollment dialog =====
    {
      selector: '[data-help="hr-enrollment-form-enrollment-date"]',
      section: "Edit enrollment dialog",
      context: "enrollment-dialog",
      title: "Enroll the person or change the start date",
      body: 'Setting an "Enrollment Date" on an eligibility enrolls the person from that date; clearing it turns the enrollment back into an eligibility.',
    },
    {
      selector: '[data-help="hr-enrollment-form-cancellation-date"]',
      section: "Edit enrollment dialog",
      context: "enrollment-dialog",
      title: "Cancel an enrollment",
      body: 'Set the "Cancelation Date" when the enrollment ends and explain why in "Reason For Cancellation" below. The line turns red from that date.',
    },
    {
      selector: '[data-help="hr-enrollment-form-consents"]',
      section: "Edit enrollment dialog",
      context: "enrollment-dialog",
      title: "Manage the enrollment's consent documents",
      body: '"Consent Documents" opens the documents attached to this enrollment, where you can add files and choose their type. Changes there are saved immediately.',
    },
    {
      selector: '[data-help="hr-enrollment-form-save"]',
      section: "Edit enrollment dialog",
      context: "enrollment-dialog",
      title: "Save the enrollment",
      body: "Saves the dates, flags and payment terms. The eligibility dates, cancellation possibility and payment fields are required; as when granting eligibility, an age-limit notice must be overridden with its switch before you can save.",
    },
    // ===== Import employees =====
    {
      selector: '[data-help="hr-import-file"]',
      section: "Import employees",
      context: "import-dialog",
      title: "Choose the filled-in template",
      body: 'Drop the Excel file here or click to browse. Use a template downloaded with "Generate Import Template" for this client, so the package columns match.',
    },
    {
      selector: '[data-help="hr-import-send-invitations"]',
      section: "Import employees",
      context: "import-dialog",
      title: "Decide whether to send invitations",
      body: '"Send Invitation Mail" (ticked by default) emails the sign-up invitation to every employee newly created by this import. Existing employees and dependents get no email.',
    },
    {
      selector: '[data-help="hr-import-submit"]',
      section: "Import employees",
      context: "import-dialog",
      title: "Run the import",
      body: '"Import" creates or updates employees and dependents row by row and adds the marked eligibilities and enrollments. You receive a result report by email, and the import with its report is listed in Action History.',
    },
    // ===== Enrollments report by period =====
    {
      selector: '[data-help="hr-report-period-dates"]',
      section: "Enrollments report by period",
      context: "report-period-dialog",
      title: "Pick the report period",
      body: 'Both "From" and "To" are required. The report lists enrollments whose enrollment date falls between them.',
    },
    {
      selector: '[data-help="hr-report-period-generate"]',
      section: "Enrollments report by period",
      context: "report-period-dialog",
      title: "Generate the period report",
      body: '"Generate" builds the Excel file, downloads it and closes the dialog. Premiums in this report are pro-rata for the period.',
    },
    // ===== Export consents =====
    {
      selector: '[data-help="hr-export-consents-salary-deduction"]',
      section: "Export consents",
      context: "export-consents-dialog",
      title: "Choose which consents to export",
      body: 'Tick "Is Salary Deduction Consent" to export salary-deduction consents instead of the other consent documents.',
    },
    {
      selector: '[data-help="hr-export-consents-submit"]',
      section: "Export consents",
      context: "export-consents-dialog",
      title: "Start the consent export",
      body: '"Export" starts the export in the background and closes the dialog. When the documents are zipped you receive an email with the download link. With "Only Active" on, only benefits running today are included.',
    },
    // ===== Bulk Management view =====
    {
      selector: '[data-help="hr-bulk-tag-filter"]',
      section: "Bulk Management view",
      context: "bulk-view",
      title: "Filter people by tag",
      body: 'Pick a tag and click "Assign" to show only employees with that tag. With several tags, only employees that carry all of them remain. Remove a tag chip to drop it from the filter.',
    },
    {
      selector: '[data-help="hr-bulk-employee-select"]',
      section: "Bulk Management view",
      context: "bulk-view",
      title: "Select many people at once",
      body: '"Select" offers "Select All", "Select Employees", "Select Dependents" and "Deselect All". Selected people are counted below the tree.',
    },
    {
      selector: '[data-help="hr-bulk-manage-tags"]',
      section: "Bulk Management view",
      context: "bulk-view",
      title: "Show, add or remove employee tags",
      body: '"Manage Tags" › "Toggle Tags" shows each employee\'s tags in the tree. "Add Tags" and "Remove Tags" open the tags dialog for the selected employees; they stay disabled until an employee (not only a dependent) is selected.',
    },
    {
      selector: '[data-help="hr-bulk-employee-tree"]',
      section: "Bulk Management view",
      context: "bulk-view",
      title: "Pick the people to work on",
      body: "Employees are listed with their dependents nested below; click a row or its checkbox to select it. The packages you select on the right then show one line per selected person.",
    },
    {
      selector: '[data-help="hr-bulk-employee-actions"]',
      section: "Bulk Management view",
      context: "bulk-view",
      optional: true,
      title: "Edit or delete one person from the tree",
      body: 'The "More options" button on a row offers "Edit Details" and "Delete Employee" or "Delete Dependent". Deleting is available to SuperAdmin only.',
    },
    {
      selector: '[data-help="hr-bulk-benefit-type-filter"]',
      section: "Bulk Management view",
      context: "bulk-view",
      title: "Filter packages by benefit type",
      body: 'Pick a benefit category and click "Select" to show only its packages; add more categories to widen the filter.',
    },
    {
      selector: '[data-help="hr-bulk-benefit-tree"]',
      section: "Bulk Management view",
      context: "bulk-view",
      title: "Pick the packages and read each person's status",
      body: 'Only packages that are running today are listed. Tick packages (or use "Select" above) to list every selected person under them: green means enrolled, a green border means eligible only, red means cancelled. Each person line has its own menu to make eligible, enroll, edit, remove or cancel.',
    },
    {
      selector: '[data-help="hr-bulk-actions"]',
      section: "Bulk Management view",
      context: "bulk-view",
      optional: true,
      title: "Apply an action to all selected people and packages",
      body: '"Bulk Actions" appears once a package is ticked. "Make Eligible" and "Enroll" open a dialog for everyone selected; "Remove Eligibilities" deletes eligibilities that have no enrollment yet, and "Remove Enrollments" clears the enrollment date but keeps the eligibility.',
    },
    {
      selector: '[data-help="hr-bulk-benefit-actions"]',
      section: "Bulk Management view",
      context: "bulk-view",
      optional: true,
      title: "Act on one package for all selected people",
      body: 'A ticked package\'s "More options" menu offers the bulk actions for that package only, plus "Cancel Enrollments" for the enrolled people.',
    },
    // ===== Bulk dialogs =====
    {
      selector: '[data-help="hr-bulk-eligibility-auto"]',
      section: "Bulk dialogs",
      context: "bulk-eligibility-dialog",
      title: "Let the system calculate eligibility dates",
      body: '"Is Auto Eligibility" leaves the dates empty so that they are calculated per person from the package rules. "Is Auto Enrollment" next to it marks the new eligibilities for automatic enrollment.',
    },
    {
      selector: '[data-help="hr-bulk-eligibility-date"]',
      section: "Bulk dialogs",
      context: "bulk-eligibility-dialog",
      title: "Set the same dates for everyone",
      body: "Without auto eligibility, the eligibility date, end date and cancellation possibility are required and apply to every selected person. The dialog title tells how many people and packages are affected; people who are already eligible are skipped.",
    },
    {
      selector: '[data-help="hr-bulk-eligibility-submit"]',
      section: "Bulk dialogs",
      context: "bulk-eligibility-dialog",
      title: "Run the bulk action and read the results",
      body: 'The button runs the operation for every listed person. When it finishes it turns into "Show results", which displays the per-person success or failure report; the same report is kept in Action History. The Enroll and Cancel dialogs work the same way.',
    },
    {
      selector: '[data-help="hr-bulk-enroll-date"]',
      section: "Bulk dialogs",
      context: "bulk-enroll-dialog",
      title: "Enroll everyone from one date",
      body: 'Enter the enrollment date for all selected people who are eligible but not yet enrolled, or tick "Is Auto Enrollment" to leave the date empty and mark the enrollments as automatic.',
    },
    {
      selector: '[data-help="hr-bulk-cancel-date"]',
      section: "Bulk dialogs",
      context: "bulk-cancel-dialog",
      title: "Set the cancellation date",
      body: "Required. It applies to every enrolled person in the selection.",
    },
    {
      selector: '[data-help="hr-bulk-cancel-reason"]',
      section: "Bulk dialogs",
      context: "bulk-cancel-dialog",
      title: "Give the reason for cancellation",
      body: "Required; the same reason is stored on every cancelled enrollment.",
    },
    {
      selector: '[data-help="hr-bulk-remove-confirm"]',
      section: "Bulk dialogs",
      context: "bulk-remove-dialog",
      title: "Confirm removing eligibilities or enrollments",
      body: 'Click "Apply" to run the removal for the selection, then "Show results" for the report. Removing an eligibility deletes it; removing an enrollment only clears its enrollment date.',
    },
    // ===== Employee tags dialog =====
    {
      selector: '[data-help="hr-tags-dialog-new-tag"]',
      section: "Employee tags dialog",
      context: "tags-dialog",
      title: "Create a new tag",
      body: 'When adding tags, type a new tag (up to 25 characters) and click "Add New Tag" to put it on the list. Existing tags are picked from the "Existing tags" box above.',
    },
    {
      selector: '[data-help="hr-tags-dialog-submit"]',
      section: "Employee tags dialog",
      context: "tags-dialog",
      title: "Apply the tags",
      body: '"Add Employee Tags" or "Remove Employee Tags" applies the listed tags to all selected employees. The button stays disabled until at least one tag is listed.',
    },
    // ===== Analytics view =====
    {
      selector: '[data-help="hr-analytics-report"]',
      section: "Analytics view",
      context: "analytics-view",
      title: "Explore the Power BI report",
      body: "Shows the client's Power BI report embedded in the page, filtered to the selected client. Use the report's own filters and pages; reload it with the refresh button at the top of the page.",
    },
    // ===== Action History view =====
    {
      selector: '[data-help="hr-history-column-action"]',
      section: "Action History view",
      context: "history-view",
      title: "See what was run",
      body: "Each row is one Excel import or bulk enrollment operation, with the user who started it and the time it was executed. Click a column header to sort.",
    },
    {
      selector: '[data-help="hr-history-action-document"]',
      section: "Action History view",
      context: "history-view",
      optional: true,
      title: "Open the input of an action",
      body: '"Action Document" opens the file the action worked on, for example the uploaded Excel file, in a new tab.',
    },
    {
      selector: '[data-help="hr-history-report-document"]',
      section: "Action History view",
      context: "history-view",
      optional: true,
      title: "Open the result report",
      body: '"Report Document" opens the HTML result report in a new tab, listing which rows or people were processed successfully and which failed.',
    },
    // ===== User Assignments view =====
    {
      selector: '[data-help="hr-user-assignment-add"]',
      section: "User Assignments view",
      context: "user-assignments-view",
      title: "Find or add a user",
      body: 'Type a name in "Select user" to search the directory. A user who is not yet assigned gets a row to choose the role and save (admin roles only); for existing users the list below is filtered.',
    },
    {
      selector: '[data-help="hr-user-assignment-limited"]',
      section: "User Assignments view",
      context: "user-assignments-view",
      title: "Restrict a user",
      body: 'Tick "Limited" to give the user restricted access; the server combines it with the Security view\'s per-benefit settings. "Internal" next to it is set automatically for GrECo accounts.',
    },
    {
      selector: '[data-help="hr-user-assignment-role"]',
      section: "User Assignments view",
      context: "user-assignments-view",
      title: "Choose the user's role",
      body: "Pick the user's role for this client, for example HR or Admin. The role decides which pages the user sees and what they may change.",
    },
    {
      selector: '[data-help="hr-user-assignment-save"]',
      section: "User Assignments view",
      context: "user-assignments-view",
      title: "Save an assignment",
      body: "Saves a new assignment or a changed role or flag. It stays disabled until a role is chosen and something changed; read-only admins see it disabled. The arrow next to it undoes unsaved changes.",
    },
    {
      selector: '[data-help="hr-user-assignment-delete"]',
      section: "User Assignments view",
      context: "user-assignments-view",
      title: "Remove a user's access",
      body: "Deletes the assignment after a confirmation; the user loses access to this client. Read-only admins see it disabled.",
    },
    // ===== Security view =====
    {
      selector: '[data-help="hr-security-cell"]',
      section: "Security view",
      context: "security-view",
      optional: true,
      title: "Set a user's access to one benefit",
      body: 'Each cell is one user and one benefit: "View+Edit" (default), "View" (read only) or "No Access" (the benefit and its enrollments are hidden from the user). Changed cells are marked until you save.',
    },
    {
      selector: '[data-help="hr-security-mark-row"]',
      section: "Security view",
      context: "security-view",
      optional: true,
      title: "Change many cells at once",
      body: '"Mark All" in a user row (or in a benefit column header) marks every cell in it. The next value you pick in any cell is applied to all marked cells, and the marks are then cleared.',
    },
    {
      selector: '[data-help="hr-security-save"]',
      section: "Security view",
      context: "security-view",
      title: "Save the access changes",
      body: '"Save Changes" stores all changed cells at once; "Cancel" next to it discards them. Both stay disabled until a cell has changed.',
    },
  ],
};
